<script setup lang="ts">

import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import Customer from "@/customer/interfaces/Customer";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Sparkles} from "@lucide/vue";
import StartJourneyModal
    from "@/appointment/components/consumer-appointment-timeline/start-journey/StartJourneyModal.vue";
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import useCustomerAppointments from "@/appointment/composables/useCustomerAppointments";
import TimelineLoading from "@/appointment/components/consumer-appointment-timeline/TimelineLoading.vue";
import Timeline from "@/appointment/components/consumer-appointment-timeline/Timeline.vue";
import CustomerHealthJourneyProgram
    from "@/customer/components/customer-health-journey/CustomerHealthJourneyProgram.vue";
import CustomerCertificationDialog from "@/customer/components/customer-certification/CustomerCertificationDialog.vue";

const props = defineProps<{
    customer: Customer;
    consumer: Customer | Beneficiary;
    currentProgram: number;
}>();

const router = useRouter();

const modalOpen = ref(false);

/**
 * The assisted screen, which reads the same programs as the cards below and
 * dates the whole series in one pass. Opened with the patient so the
 * pharmacist does not search for the file they are already looking at.
 */
const openHealthJourney = () => {
    router.push({name: 'agenda.health-journey', query: {customerId: props.customer.id}});
};
const certificationOpen = ref(false);
const {data: appointments, isLoading, mutate} = useCustomerAppointments(props.customer.id);

/**
 * Get the programs of type support program that are bookable and not hidden.
 */
const programs = computed(() => {
    return props.consumer.programs
        .filter(program => {
            return program.programmable_type === 'support'
                && program.id !== props.currentProgram
                && program.bookable
                && !program.feedbacks?.find(feedback => feedback.hide_notification);
        });
});

/**
 * Find the next appointment for a given support program.
 */
const appointmentForProgram = (supportProgramID: number) => {
    if (!appointments.value) {
        return null;
    }

    return appointments.value.find(
        (appointment) => appointment.appointmentable_type === 'support' && appointment.appointmentable_id === supportProgramID
    );
};
</script>

<template>
  <BaseCard>
    <BaseCardBody class="space-y-4">
      <div class="flex justify-between gap-4">
        <div>
          <h2>
            Planifiez dès aujourd'hui les prochains rendez-vous
          </h2>
          <p>
            Proposez à {{ consumer.information.name }} un parcours officinal complet, couvrant toutes ses vaccinations
            et entretiens de l'année
          </p>
        </div>
        <div class="flex shrink-0 flex-col items-end gap-1">
          <BaseButton density="comfortable" @click="openHealthJourney">
            <Sparkles class="size-5 mr-2" />
            Programmer le parcours
          </BaseButton>
          <button
            type="button"
            class="text-xs text-primary underline-offset-2 hover:underline"
            @click="modalOpen = true"
          >
            Planifier un rendez-vous à la main
          </button>
        </div>
      </div>
      <div class="space-y-4">
        <TimelineLoading v-if="isLoading" />
        <Timeline v-if="appointments?.length" :appointments="appointments" @reload="mutate" />
        <div v-if="programs?.length" class="pb-4 overflow-x-auto bg-pink-lighter pt-8 px-4 rounded-md">
          <div class="flex gap-4">
            <div v-for="program in programs" :key="program.id" class="shrink-0">
              <CustomerHealthJourneyProgram
                :program="program"
                :customer="customer"
                :appointment="appointmentForProgram(program.programmable_id)"
                @refresh="mutate"
                @open-certification="mutate(); certificationOpen = true"
              />
            </div>
          </div>
        </div>
      </div>
      <StartJourneyModal
        v-model:open="modalOpen"
        :customer="customer"
        @batch-created="mutate()"
        @open-certification-dialog="modalOpen = false; certificationOpen = true;"
      />
    </BaseCardBody>
    <CustomerCertificationDialog v-model:open="certificationOpen" :customer="customer" />
  </BaseCard>
</template>

<style scoped>
.overflow-x-auto::-webkit-scrollbar {
    height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
    background-color: #cbd5e0;
    border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background-color: #a0aec0;
}
</style>
